import {type RefObject, useRef} from 'react';

import {useIsomorphicLayoutEffect} from '@sail/engine';
import {useStableCallback} from '@sail/react';

import {getOwnerWindow} from '../utils/getOwnerWindow';

type Config = {
  node:
    | HTMLElement
    | (Window & typeof global)
    | RefObject<HTMLElement>
    | null
    | undefined;
  handler: (entry: ResizeObserverEntry) => void;
  avoidInitialEvent?: boolean;
  box?: ResizeObserverBoxOptions;
};

type DOMRectConstructor = new (
  x?: number,
  y?: number,
  width?: number,
  height?: number,
) => DOMRectReadOnly;

type WindowWithDOMRectConstructors = Window & {
  DOMRect?: DOMRectConstructor;
  DOMRectReadOnly?: DOMRectConstructor;
};

const callbacks = new Map<
  ResizeObserverBoxOptions,
  WeakMap<Element, Set<Config['handler']>>
>();

const getCallbacks = (box: ResizeObserverBoxOptions) => {
  let boxCallbacks = callbacks.get(box);

  if (!boxCallbacks) {
    boxCallbacks = new WeakMap();
    callbacks.set(box, boxCallbacks);
  }

  return boxCallbacks;
};

const resolveNode = (nodeOrRef: Config['node']) =>
  (nodeOrRef && 'current' in nodeOrRef ? nodeOrRef.current : nodeOrRef) ?? null;

const resizeObservers = new WeakMap<
  Window,
  Map<ResizeObserverBoxOptions, ResizeObserver>
>();

const getResizeObserver = (
  ownerWindow: Window & typeof global,
  box: ResizeObserverBoxOptions,
) => {
  let windowObservers = resizeObservers.get(ownerWindow);

  if (!windowObservers) {
    windowObservers = new Map();
    resizeObservers.set(ownerWindow, windowObservers);
  }

  let resizeObserver = windowObservers.get(box);

  if (!resizeObserver) {
    resizeObserver = new ownerWindow.ResizeObserver((entries) => {
      const boxCallbacks = getCallbacks(box);
      entries.forEach((entry) => {
        const elementCallbacks = boxCallbacks.get(entry.target);
        elementCallbacks?.forEach((callback) => callback(entry));
      });
    });
    windowObservers.set(box, resizeObserver);
  }

  return resizeObserver;
};

const constructDOMRect = (
  Constructor: DOMRectConstructor | undefined,
  width: number,
  height: number,
) => {
  if (typeof Constructor !== 'function') {
    return undefined;
  }

  try {
    return new Constructor(0, 0, width, height);
  } catch {
    return undefined;
  }
};

const createStructuralDOMRect = (
  width: number,
  height: number,
): DOMRectReadOnly => {
  const rect = {
    bottom: height,
    height,
    left: 0,
    right: width,
    top: 0,
    width,
    x: 0,
    y: 0,
  };

  return {...rect, toJSON: () => ({...rect})};
};

// This synthetic entry represents the viewport. `document.body` only satisfies
// the required Element target; its dimensions are not measured.
const createWindowResizeObserverEntry = (
  ownerWindow: Window,
): ResizeObserverEntry => {
  // eslint-disable-next-line @sail/ui-no-reflow
  const width = ownerWindow.innerWidth;
  // eslint-disable-next-line @sail/ui-no-reflow
  const height = ownerWindow.innerHeight;
  const devicePixelRatio = ownerWindow.devicePixelRatio;
  const devicePixelWidth = Math.round(width * devicePixelRatio);
  const devicePixelHeight = Math.round(height * devicePixelRatio);
  const {DOMRect, DOMRectReadOnly} =
    ownerWindow as WindowWithDOMRectConstructors;
  const contentRect =
    constructDOMRect(DOMRectReadOnly, width, height) ??
    constructDOMRect(DOMRect, width, height) ??
    createStructuralDOMRect(width, height);

  return {
    borderBoxSize: [{blockSize: height, inlineSize: width}],
    contentBoxSize: [{blockSize: height, inlineSize: width}],
    contentRect,
    devicePixelContentBoxSize: [
      {
        blockSize: devicePixelHeight,
        inlineSize: devicePixelWidth,
      },
    ],
    target: ownerWindow.document.body,
  };
};

export function useResizeObserver({
  node: nodeOrRef,
  handler,
  avoidInitialEvent = false,
  box = 'border-box',
}: Config): void {
  const hasCalled = useRef(false);
  const subscription = useRef<{
    avoidInitialEvent: boolean;
    box: ResizeObserverBoxOptions;
    cleanup?: () => void;
    node: ReturnType<typeof resolveNode>;
  }>();
  const stableHandler = useStableCallback(handler);

  // Runs every render (no deps) to pick up ref.current changes after React commits.
  useIsomorphicLayoutEffect(() => {
    const node = resolveNode(nodeOrRef);
    const currentSubscription = subscription.current;

    if (
      currentSubscription?.node === node &&
      currentSubscription.avoidInitialEvent === avoidInitialEvent &&
      currentSubscription.box === box
    ) {
      return;
    }

    currentSubscription?.cleanup?.();

    const ownerWindow = getOwnerWindow(node);
    let cleanup: (() => void) | undefined;

    // We can't use `ResizeObserver` on window, but for consistency we polyfill it with a similar API
    if (node === ownerWindow) {
      const func = () => {
        stableHandler(createWindowResizeObserverEntry(ownerWindow));
      };

      if (!avoidInitialEvent) {
        func();
      }

      ownerWindow.addEventListener('resize', func);
      cleanup = () => ownerWindow.removeEventListener('resize', func);
    } else if (node instanceof ownerWindow.Element) {
      const resizeObserver = getResizeObserver(ownerWindow, box);
      const boxCallbacks = getCallbacks(box);

      if (!boxCallbacks.has(node)) {
        boxCallbacks.set(node, new Set());
      }

      const elementCallbacks = boxCallbacks.get(node);

      const callback = (entry: ResizeObserverEntry) => {
        if (!avoidInitialEvent || hasCalled.current) {
          stableHandler(entry);
        }

        hasCalled.current = true;
      };

      if (elementCallbacks?.size === 0) {
        resizeObserver.observe(node, {box});
      }

      elementCallbacks?.add(callback);
      cleanup = () => {
        hasCalled.current = false;
        elementCallbacks?.delete(callback);

        if (elementCallbacks?.size === 0) {
          resizeObserver.unobserve(node);
        }
      };
    } else if (node !== null && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('useResizeObserver: unrecognized type for node', node);
    }

    subscription.current = {avoidInitialEvent, box, cleanup, node};
  });

  useIsomorphicLayoutEffect(
    () => () => {
      subscription.current?.cleanup?.();
      subscription.current = undefined;
    },
    [],
  );
}
